import type { HTMLAttributes, ReactNode } from "react";
import { cx } from "@/lib/cx";

export type StatProps = HTMLAttributes<HTMLDivElement> & {
  value: ReactNode;
  label: ReactNode;
  size?: "sm" | "md" | "lg";
  inline?: boolean;
  /**
   * `body` is the default sans caption. `label` switches to the mono uppercase
   * annotation used when a figure sits on a photographic plate.
   */
  labelTone?: "body" | "label";
};

const scale = {
  sm: { value: "text-[16px]", label: "text-[13px]" },
  md: { value: "text-[28px]", label: "text-[14px]" },
  lg: { value: "text-[clamp(32px,3.2vw,44px)]", label: "text-[15px]" },
} as const;

/**
 * Numbers carry the argument in this system — the value is always signal red
 * and always keeps its `+` suffix verbatim.
 */
export function Stat({
  value,
  label,
  size = "md",
  inline = false,
  labelTone = "body",
  className,
  ...rest
}: StatProps) {
  const s = scale[size];
  return (
    <div
      className={cx("flex", inline ? "flex-row items-center gap-2" : "flex-col items-start gap-1", className)}
      {...rest}
    >
      <span
        className={cx("text-signal-500 font-sans font-semibold leading-[1.05] tracking-[-0.01em]", s.value)}
      >
        {value}
      </span>
      <span
        className={cx(
          labelTone === "label"
            ? "font-label text-label uppercase text-text-muted"
            : cx("text-text-body leading-[1.35]", s.label, size === "lg" ? "uppercase tracking-[0.02em]" : "tracking-[0]"),
        )}
      >
        {label}
      </span>
    </div>
  );
}
